import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { fetcher } from '@blockworks/platform/api';
import { useLocalStorage } from '@blockworks/platform/client';

interface AlgoliaContextValue {
    algoliaKey: string | null;
    loading: boolean;
    error: boolean;
}

const AlgoliaContext = createContext<AlgoliaContextValue | undefined>(undefined);

interface AlgoliaProviderProps {
    children: React.ReactNode;
}

const ALGOLIA_KEY_STORAGE = 'algoliaKey';
const ALGOLIA_TTL_STORAGE = 'algoliaTTL';
const ALGOLIA_CACHE_BUSTER_KEY = 'algoliaLocalStorageCacheBuster';

export const AlgoliaProvider = ({ children }: AlgoliaProviderProps) => {
    const { storedValue: algoliaKey, setValue: setAlgoliaKey } = useLocalStorage<string | null>(
        ALGOLIA_KEY_STORAGE,
        null,
    );
    const { storedValue: storedTTL, setValue: setStoredTTL } = useLocalStorage<number | null>(
        ALGOLIA_TTL_STORAGE,
        null,
    );
    const { storedValue: storedLocalStorageCacheBuster, setValue: setStoredLocalStorageCacheBuster } = useLocalStorage<
        number | null
    >(ALGOLIA_CACHE_BUSTER_KEY, null);

    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<boolean>(false);

    /**
     * This effect runs once on component mount and handles the scenario where
     * the root Algolia API key stored in Redis might be manually deleted or invalidated,
     * such as in accidental deletion or other administrative cases.
     *
     * It fetches the current cache buster string from the backend (`/api/algolia/cache-buster`)
     * which is tied to the validity of the Algolia key.
     *
     * If the fetched cache buster differs from what the client has stored locally,
     * it indicates the cached Algolia key is no longer valid (e.g., key was deleted in Redis),
     * so the effect clears the local Algolia key and its TTL, forcing the app to refetch a fresh key.
     *
     * This mechanism ensures that the client automatically detects and recovers from manual
     * invalidation of the Algolia key on the server by busting its local cache accordingly.
     */
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const fetchCacheBuster = async () => {
            try {
                const response = await fetch('/api/algolia/cache-buster');
                if (!response.ok) {
                    console.warn('Algolia Failed to fetch cache buster');
                    return;
                }
                const data = await response.json();
                const cacheBusterFromAPI = data.cacheBuster;

                if (!cacheBusterFromAPI) return;

                if (storedLocalStorageCacheBuster !== cacheBusterFromAPI) {
                    console.log('Algolia Cache buster mismatch detected — busting cache.');

                    setAlgoliaKey(null);
                    setStoredTTL(null);
                    setStoredLocalStorageCacheBuster(cacheBusterFromAPI);
                }
            } catch (error) {
                console.error('Algolia Error fetching cache buster:', error);
            }
        };

        fetchCacheBuster();
    }, []);

    useEffect(() => {
        let isActive = true;
        const retryAttempts = 3;
        const retryDelay = 1000;

        const fetchAlgoliaKey = async () => {
            try {
                setLoading(true);
                let attempts = 0;
                let error;

                while (attempts < retryAttempts) {
                    try {
                        const response = await fetcher<false, void, { apiKey: string; expirationTimestamp: number }>({
                            url: '/api/algolia/secured-key',
                            method: fetcher.Method.Post,
                        });

                        const { apiKey, expirationTimestamp } = response;

                        if (isActive) {
                            setAlgoliaKey(apiKey);
                            setStoredTTL(expirationTimestamp);
                            setError(false);
                        }
                        return;
                    } catch (e) {
                        error = e;
                        attempts++;
                        if (attempts < retryAttempts) {
                            await new Promise(resolve => setTimeout(resolve, retryDelay));
                        }
                    }
                }
                throw error;
            } catch (err: unknown) {
                if (isActive) {
                    console.error(err);
                    setError(true);
                }
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        // Edge case when user deletes storedTTL or is missing for some reason
        if (!storedTTL) {
            fetchAlgoliaKey();
            return;
        }

        // Check if the apiKey or TTL is missing or expired
        const isExpired = storedTTL !== null && Math.floor(Date.now() / 1000) >= storedTTL;

        if (!algoliaKey || isExpired) {
            fetchAlgoliaKey();
            return;
        }

        // Refetches a new key when current key expires
        if (storedTTL !== null && storedTTL > 0) {
            const remainingTime = storedTTL - Math.floor(Date.now() / 1000);
            const refreshTime = Math.max((remainingTime - 5) * 1000, 0); // Refresh 5 seconds before expiration

            const refreshTimeout = setTimeout(() => {
                fetchAlgoliaKey();
            }, refreshTime);

            return () => {
                isActive = false;
                clearTimeout(refreshTimeout);
            };
        }

        return () => {
            isActive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [algoliaKey, storedTTL]);

    const value: AlgoliaContextValue = useMemo(
        () => ({
            algoliaKey,
            loading,
            error,
        }),
        [algoliaKey, error, loading],
    );

    return <AlgoliaContext.Provider value={value}>{children}</AlgoliaContext.Provider>;
};

export const useAlgolia = (): AlgoliaContextValue => {
    const context = useContext(AlgoliaContext);
    if (!context) {
        throw new Error('useAlgolia must be used within an AlgoliaProvider');
    }
    return context;
};
